import { tintRgbTohex } from "components/ColorItem/ColorItem"
import { print } from "graphql"
import { clearProductSlice } from "store/product/productSlice"
import { store } from "store/store"
import { clearUserInfo, userExpired } from "store/user/userSlice"
import {
  Brand,
  Cover,
  GlassesProduct,
  GlassesRenderingOptions,
  IntegrationType,
  MerchantData,
  MerchantId,
  ProductV2,
  UserData,
} from "types"
import { parseAwsTokenToObject } from "utils/helper"
import { convertGlassesZipToObject, createGlassesCustomZipEffect } from "utils/zipEffectFileReader"
import { typedEnv } from "../config/typedEnv"
import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  Observable,
} from "@apollo/client"
import { setContext } from "@apollo/client/link/context"
import { ErrorResponse, onError } from "@apollo/client/link/error"
import { Sha256 } from "@aws-crypto/sha256-js"
import { HttpRequest } from "@aws-sdk/protocol-http"
import { SignatureV4 } from "@aws-sdk/signature-v4"
import * as schema from "./schema.graphql"
import { ProductType } from "store/product/types"
import { ProductCategories } from "components/Navbar/types"

interface IAWSToken {
  awsAccessKey: string
  awsJwtToken: string
  awsSecretAccessKey: string
}

export class TintVTOService {
  endpoint: string = `https://${typedEnv.VITE_ADMIN_ENDPOINT}/graphql`
  apiKey: string = typedEnv.VITE_ADMIN_API_KEY
  userType: IntegrationType = IntegrationType.COMMON
  awsToken: IAWSToken | null
  gqlClient: ApolloClient<NormalizedCacheObject>

  constructor(userType: IntegrationType = IntegrationType.COMMON) {
    this.userType = userType
    this.updateGQLClient()
  }

  changeUserType(userT: IntegrationType) {
    this.userType = userT
    this.updateGQLClient()
  }

  changeAwsToken(token: IAWSToken) {
    this.awsToken = token
    this.updateGQLClient()
  }

  changeAwsTokenAndUserType(token: string | null, userT: IntegrationType) {
    if (token) this.awsToken = parseAwsTokenToObject(token)
    else this.awsToken = null

    this.changeUserType(userT)
    this.updateGQLClient()
  }

  updateGQLClient() {
    const httpLink = createHttpLink({
      uri: this.endpoint,
    })

    const logoutLink = onError(({ networkError }: ErrorResponse): void => {
      if (networkError && "statusCode" in networkError) {
        if (
          (networkError.statusCode === 403 || networkError.statusCode === 401) &&
          this.userType !== IntegrationType.COMMON
        ) {
          this.logoutUserExpired()
          return
        }
        if (this.userType === IntegrationType.COMMON && networkError.statusCode === 401) {
          this.gqlClient.stop()
          this.logout()
          return
        }
      }
    })

    const authLink = this.createAuthLink()

    this.gqlClient = new ApolloClient({
      link: ApolloLink.from([logoutLink, authLink, httpLink]),
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: "no-cache",
          errorPolicy: "ignore",
        },
        query: {
          fetchPolicy: "no-cache",
          errorPolicy: "all",
        },
      },
    })
  }

  createSignedAWSRequest = async (
    uri: string,
    operationName: string,
    query: string,
    variables: Record<string, string> = {}
  ) => {
    const url = new URL(uri)
    const requestUri = url.href.includes("/graphql") ? url.href : `${url.origin}/graphql`
    const hostname = url.hostname
    const path = url.pathname.includes("/graphql") ? url.pathname : "/graphql"

    const request = new HttpRequest({
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        host: hostname,
      },
      body: JSON.stringify({ operationName, query, variables }),
      hostname: hostname,
      path: path,
    })

    const signer = new SignatureV4({
      credentials: {
        accessKeyId: this.awsToken.awsAccessKey,
        secretAccessKey: this.awsToken.awsSecretAccessKey,
        sessionToken: this.awsToken.awsJwtToken,
      },
      region: "us-east-1",
      service: "appsync",
      sha256: Sha256,
    })

    const signedRequest = await signer.sign(request)

    return new Request(requestUri, {
      method: signedRequest.method,
      headers: signedRequest.headers,
      body: signedRequest.body,
    })
  }

  createAuthLink(): ApolloLink {
    if (this.userType === IntegrationType.TIENDANUBE)
      return new ApolloLink((operation) => {
        return new Observable((observer) => {
          ;(async () => {
            try {
              const { operationName, query, variables } = operation
              const operationString = print(query)

              const signedRequest = await this.createSignedAWSRequest(
                this.endpoint,
                operationName,
                operationString || "",
                variables
              )
              const response = await fetch(signedRequest)
              if (!response.ok) {
                const error = new Error(`HTTP error: ${response.status}`) as any
                error.statusCode = response.status
                error.response = response
                throw error
              }

              const result = await response.json()
              observer.next(result)
              observer.complete()
            } catch (error) {
              observer.error(error)
              throw error
            }
          })()
        })
      })
    else
      return setContext(async ({ operationName }, { headers }) => {
        return {
          headers: {
            ...headers,
            ...this.getHeaders(
              operationName === "LoginMutation" || operationName === "loginFromMiddle"
            ),
          },
        }
      })
  }

  getHeaders(login: boolean) {
    if (login)
      return {
        "Content-Type": "application/json",
        Accept: "application/json",
        "x-api-key": this.apiKey,
      }

    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      Accept: "application/json",
    }
  }

  async authorizeCDN() {
    return await this.gqlClient.query({
      query: schema.AuthorizeCDNQuery,
    })
  }

  async uploadCDN(params: { body: BodyInit; name: string; type: string; jwt: string }) {
    try {
      await fetch(`https://${typedEnv.VITE_CDN_UPLOAD_ENDPOINT}/${params.name}?jwt=${params.jwt}`, {
        method: "PUT",
        headers: {
          "Content-Type": params.type,
        },
        body: params.body,
      })
    } catch (e) {
      console.log(e)
    }
    return `https://${typedEnv.VITE_CDN_ENDPOINT}/${params.name}`
  }

  async deleteCDN(params: { name: string; jwt: string }) {
    try {
      await fetch(`https://${typedEnv.VITE_CDN_UPLOAD_ENDPOINT}/${params.name}?jwt=${params.jwt}`, {
        headers: {
          "Content-Type": "application/json",
        },
        method: "DELETE",
      })
    } catch (e) {
      console.log(e)
    }
  }

  async getMerchantCdnConfig(merchantId: string) {
    try {
      const response = await fetch(
        `https://cdn.tintvto.sdk.banuba.net/merchants/${merchantId}/config.json`
      )

      const data = await response.json()
      const config = JSON.parse(window.atob(data.config) ?? "")

      return config
    } catch (e) {
      return null
    }
  }

  async types() {
    return await this.gqlClient.query({
      query: schema.ProductTypesQuery,
    })
  }

  async categories() {
    return await this.gqlClient.query({
      query: schema.ProductCategoriesQuery,
    })
  }

  async saveMerchant(merchant: MerchantData) {
    const merchantId = merchant.id ? merchant.id : ""

    return await this.gqlClient.mutate({
      mutation: schema.SaveMerchantMutation,
      variables: {
        merchantId,
        name: merchant.name,
        email: merchant.email,
        domain: merchant.domain,
        planId: merchant.planId,
        cognitoSub: merchant.cognitoSub,
        description: merchant.description,
        brandsIds: merchant.brandsIds,
        token: merchant.token ? merchant.token : "",
      },
    })
  }

  async registerUser({ username, password, group }: UserData) {
    return await this.gqlClient.mutate({
      mutation: schema.RegisterUserMutation,
      variables: {
        username,
        password,
        group,
      },
    })
  }

  async updateUser({ username, password }: { username: string; password: string }) {
    return await this.gqlClient.mutate({
      mutation: schema.UpdateUserMutation,
      variables: {
        username,
        password,
      },
    })
  }

  async deleteUser(username: string) {
    return await this.gqlClient.mutate({
      mutation: schema.DeleteUserMutation,
      variables: {
        username,
      },
    })
  }

  async login(login: string, password: string) {
    let responseJson

    try {
      responseJson = await this.gqlClient.mutate({
        mutation: schema.LoginMutation,
        variables: {
          login,
          password,
        },
      })
    } catch (e) {
      return
    }

    if (!responseJson?.data || responseJson.errors) return

    if (responseJson.data.login)
      localStorage.setItem("accessToken", responseJson.data.login.accessToken)

    return responseJson.data.login
  }

  async fetchAllProducts(merchantId: MerchantId, category: ProductCategories) {
    const allProducts: ProductType[] = []

    const { data } = await this.productsV2(0, "", category, merchantId)

    const firstPageItems = data?.productsV2?.items || []
    allProducts.push(...firstPageItems)

    const totalPages = data?.productsV2?.pageInfo?.pageQty || 1

    if (totalPages <= 1) return allProducts

    const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 1)
    const pageRequests = remainingPages.map((page) =>
      this.productsV2(page, "", category, merchantId)
    )

    const responses = await Promise.all(pageRequests)

    responses.forEach(({ data }) => {
      const items = data?.productsV2?.items || []
      allProducts.push(...items)
    })

    return allProducts
  }

  async loginTiendanube(token: string) {
    let responseJson
    try {
      responseJson = await this.gqlClient.mutate({
        mutation: schema.loginFromMiddle,
        variables: {
          token,
        },
      })
    } catch (e) {
      responseJson = false
    }

    if (!responseJson?.data?.getAccess || responseJson.errors) return false

    responseJson = responseJson.data.getAccess

    return responseJson
  }

  logout() {
    localStorage.removeItem("accessToken")
    store.dispatch(clearUserInfo())
    store.dispatch(clearProductSlice())
    this.changeAwsTokenAndUserType(null, IntegrationType.COMMON)
  }

  logoutUserExpired() {
    localStorage.removeItem("accessToken")
    store.dispatch(userExpired())
    store.dispatch(clearProductSlice())
    this.changeAwsToken(null)
  }

  userDetails() {
    const accessToken = localStorage.getItem("accessToken") as string
    if (!accessToken) return {}

    const data = JSON.parse(window.atob(accessToken.split(".")[1]))

    return {
      sub: data.sub,
      username: data.username,
      groups: data["cognito:groups"],
    }
  }

  async userBrand() {
    const accessToken = localStorage.getItem("accessToken") as string
    if (!accessToken) throw new Error("Token does not exist")

    const data = JSON.parse(window.atob(accessToken.split(".")[1]))
    const { data: brand } = await this.brandById("", data.sub)
    return brand as { name: string }
  }

  async listUsers() {
    return await this.gqlClient.mutate({
      mutation: schema.ListUsersMutation,
    })
  }

  async plans() {
    return fetch(`https://${typedEnv.VITE_BILLING_TINTVTO_SERVICE_ENDPOINT}/plans`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
  }

  async merchantById(merchantId: string) {
    return await this.gqlClient.query({
      query: schema.MerchantByIdQuery,
      variables: {
        merchantId,
      },
    })
  }

  async resolveMerchant(cognito: string) {
    return await this.gqlClient.query({
      query: schema.resolveMerchant,
      variables: {
        id: cognito,
      },
    })
  }

  async merchants() {
    return await this.gqlClient.query({
      query: schema.MerchantsQuery,
    })
  }

  async products(page: number, partial: string, brand: string) {
    return await this.gqlClient.query({
      query: schema.ProductsQuery,
      variables: {
        page,
        partial,
        brand,
        adminMode: !brand,
      },
    })
  }

  async productsV2(page: number, partial: string, category: string, merchant: string) {
    return await this.gqlClient.query({
      query: schema.ProductsQueryV2,
      variables: {
        page,
        partial,
        category,
        merchant,
      },
    })
  }

  async lookById(lookId: string) {
    return await this.gqlClient.query({
      query: schema.LookQuery,
      variables: {
        lookId,
      },
    })
  }

  async productByIdV2(productId: string): Promise<ProductV2> {
    const { data } = await this.gqlClient.query({
      query: schema.ProductQueryV2,
      variables: {
        productId,
      },
    })

    let coverLeft: Cover,
      coverFront: Cover,
      coverRight: Cover,
      renderingOptions: GlassesRenderingOptions

    if (data.productV2?.skus[0]?.vto.effectUrl) {
      const res = await convertGlassesZipToObject(data.productV2?.skus[0]?.vto.effectUrl)
      coverLeft = res.coverLeft
      coverFront = res.coverFront
      coverRight = res.coverRight
      renderingOptions = res.renderingOptions
    }

    return {
      upc: "",
      id: data.productV2.id,
      name: data.productV2.name,
      cover: data.productV2.cover,
      brand: data.productV2.brand,
      brandSlug: data.productV2.brandSlug,
      type: data.productV2.type,
      categoryId: data.productV2.category.id,
      sku: data.productV2.skus[0].sku,
      region: data.productV2.skus[0].vto.region,
      coverage: data.productV2.skus[0].vto.coverage,
      finish: data.productV2.skus[0].vto.finish,
      url: data.productV2.skus[0].url,
      effectUrl: data.productV2.skus[0].vto.effectUrl,
      coverLeft,
      coverFront,
      coverRight,
      renderingOptions,
      colors: data.productV2.skus.map((sku) => {
        return {
          color: sku.vto.color[0] !== "#" ? tintRgbTohex(sku.vto.color) : sku.vto.color,
          upc: sku.upc,
          sku: sku.sku,
          skuId: sku.id,
          externalId: sku.externalId ?? null,
          name: sku.name,
        }
      }),
      merchant: data.productV2.merchant ?? "",
    }
  }

  async brands(): Promise<Brand[]> {
    const data = await this.gqlClient.query({
      query: schema.BrandsQuery,
    })
    return data?.data?.brands || []
  }

  async brandById(id: string, sub: string) {
    return await this.gqlClient.query({
      query: schema.BrandByIdQuery,
      variables: {
        id,
        sub,
      },
    })
  }

  async looks() {
    return await this.gqlClient.query({
      query: schema.AllLooksQuery,
    })
  }

  async saveLook(look: any) {
    const selected: any[] = []

    if (look.products.selectedSkus)
      look.products.selectedSkus.forEach((sku: any) => {
        selected.push({
          sku: sku.id,
          product: sku.productId,
        })
      })

    let cover = look.cover

    if (look.file) {
      const {
        data: { authorizeCDN },
      } = await this.authorizeCDN()

      cover = await this.uploadCDN({
        body: look.file,
        name: look.file.name,
        type: look.file.type,
        jwt: authorizeCDN,
      })
    }

    return await this.gqlClient.mutate({
      mutation: schema.SaveLookMutation,
      variables: {
        lookId: look.id,
        cover,
        name: look.name,
        products: selected,
      },
    })
  }

  async deleteLook(lookId: string) {
    return await this.gqlClient.mutate({
      mutation: schema.DeleteLookMutation,
      variables: {
        lookId,
      },
    })
  }

  async deleteProduct(productId: string) {
    return await this.gqlClient.mutate({
      mutation: schema.DeleteProductMutation,
      variables: {
        productId,
      },
    })
  }

  cdnImgUrlName(productItem) {
    return `imgs/products/${productItem.merchant || productItem.merchantId}/${
      productItem.file.name ? productItem.file.name : new Date().getTime()
    }`
  }

  cdnEffectUrlName(productItem) {
    return `effect/products/${
      productItem.merchant || productItem.merchantId
    }/effect_${new Date().getTime()}.zip`
  }

  async saveProductV2(productItem: ProductV2) {
    const productId = productItem.id

    let cover = productItem.cover

    if (productItem.file) {
      const {
        data: { authorizeCDN },
      } = await this.authorizeCDN()

      cover = await this.uploadCDN({
        body: productItem.file,
        name: this.cdnImgUrlName(productItem),
        type: productItem.file.type,
        jwt: authorizeCDN,
      })
    }

    let skus
    if (productItem.colors.length > 0)
      skus = productItem.colors.map(
        (objColor: {
          color: string
          upc: string
          sku: string
          skuId?: string
          externalId?: string
          name: string
        }) => ({
          id: objColor.skuId ?? "",
          externalId: objColor.externalId ?? null,
          sku: objColor.sku,
          upc: objColor.upc,
          name: objColor.name,
          productId: productItem.id,
          url: productItem.url,
          vto: {
            color: objColor.color.replaceAll(",", "").replaceAll(" ", ""),
            coverage: productItem.coverage,
            finish: productItem.finish,
            region: productItem.region,
          },
        })
      )
    else
      skus = [
        {
          id: "",
          sku: "",
          upc: "",
          name: "",
          productId: productItem.id,
          url: productItem.url,
          vto: {
            color: "",
            coverage: productItem.coverage,
            finish: productItem.finish,
            region: productItem.region,
          },
        },
      ]

    return await this.gqlClient.mutate({
      mutation: schema.SaveProductMutationV2,
      variables: {
        externalId: productItem.externalId ?? null,
        name: productItem.name,
        merchant: productItem.merchant,
        id: productId,
        brand: productItem.brand,
        categoryId: productItem.categoryId,
        typeId: productItem.typeId,
        cover,
        price: {
          amount: 0,
          currency: "",
        },
        skus,
      },
    })
  }

  async saveGlassesV2(productItem: GlassesProduct, configJson: string) {
    const productId = productItem.id ? productItem.id : ""

    let cover = productItem.cover
    let effectUrl = productItem.effectUrl
    let authorizeCDN
    if (
      (productItem.coverLeft && productItem.coverFront && productItem.coverRight) ||
      (productItem.cover && productItem.file)
    ) {
      const { data } = await this.authorizeCDN()
      authorizeCDN = data?.authorizeCDN
    }
    const renderingOptions = {
      scale: productItem?.renderingOptions?.scale || { x: 1, y: 1, z: 1 },
      positionAccuracy: productItem?.renderingOptions?.positionAccuracy || { x: 0, y: 2, z: 5 },
    }
    if (productItem.file)
      cover = await this.uploadCDN({
        body: productItem.file,
        name: this.cdnImgUrlName(productItem),
        type: productItem.file.type,
        jwt: authorizeCDN,
      })

    if (productItem.coverLeft && productItem.coverFront && productItem.coverRight) {
      const zipBlob = await createGlassesCustomZipEffect({
        coverLeft: productItem.coverLeft,
        coverFront: productItem.coverFront,
        coverRight: productItem.coverRight,
        renderingOptions,
        configJson,
      })

      effectUrl = await this.uploadCDN({
        body: zipBlob,
        name: this.cdnEffectUrlName(productItem),
        type: "application/zip",
        jwt: authorizeCDN,
      })
    }

    let skus
    if (productItem.colors && productItem.colors.length > 0)
      skus = productItem.colors.map(
        (objColor: {
          color: string
          upc: string
          sku: string
          skuId?: string
          externalId?: string
          name: string
        }) => ({
          id: objColor.skuId ?? "",
          externalId: objColor.externalId ?? null,
          sku: objColor.sku || productItem.sku || "",
          upc: objColor.upc,
          name: objColor.name,
          productId: productItem.id,
          url: productItem.url,
          vto: {
            color: objColor.color.replaceAll(",", "").replaceAll(" ", ""),
            coverage: productItem.coverage || "",
            finish: productItem.finish || "",
            region: productItem.region || "",
            effectUrl,
          },
        })
      )
    else
      skus = [
        {
          id: "",
          sku: productItem.sku ?? "",
          upc: "",
          name: "",
          productId: productItem.id,
          url: productItem.url,
          vto: {
            color: "",
            coverage: productItem.coverage || "",
            finish: productItem.finish || "",
            region: productItem.region || "",
            effectUrl,
          },
        },
      ]

    const variables = {
      externalId: productItem.externalId ?? null,
      name: productItem.name,
      merchant: productItem.merchant,
      id: productId,
      brand: productItem.brand,
      categoryId: productItem.categoryId,
      typeId: productItem.typeId,
      cover,
      price: {
        amount: 0,
        currency: "",
      },
      skus,
    }

    return await this.gqlClient.mutate({
      mutation: schema.SaveProductMutationV2,
      variables,
    })
  }

  async toggleProductVisibility(productId: string, visible: boolean) {
    return await this.gqlClient.mutate({
      mutation: schema.ToggleProductVisibilityMutation,
      variables: {
        productId,
        visible,
      },
    })
  }

  async toggleLookVisibility(lookId: string, visible: boolean) {
    return await this.gqlClient.mutate({
      mutation: schema.ToggleLookVisibilityMutation,
      variables: {
        lookId,
        visible,
      },
    })
  }

  async saveBrand(brand: Brand) {
    return await this.gqlClient.mutate({
      mutation: schema.SaveBrandMutation,
      variables: {
        brandId: brand.id || "",
        slug: brand.slug,
        name: brand.name,
        sub: brand.sub,
      },
    })
  }

  async searchProducts(partial: string) {
    return await this.gqlClient.query({
      query: schema.SearchProductsQuery,
      variables: {
        partial,
      },
    })
  }

  async updateUICustomization({ merchantId, data, mainLogo, shortLogo }) {
    if (mainLogo === "" || shortLogo === "") {
      const {
        data: { authorizeCDN },
      } = await this.authorizeCDN()
      if (mainLogo === "")
        await this.deleteCDN({ name: `merchants/${merchantId}/logo.png`, jwt: authorizeCDN })

      if (shortLogo === "")
        await this.deleteCDN({ name: `merchants/${merchantId}/short-logo.png`, jwt: authorizeCDN })
    }
    return await this.gqlClient.mutate({
      mutation: schema.SaveStylesMutation,
      variables: {
        merchantId,
        data: JSON.stringify(data),
        mainLogo: mainLogo || "",
        shortLogo: shortLogo || "",
      },
    })
  }

  async getUICustomization(merchantId) {
    return await this.gqlClient.query({
      query: schema.GetStylesQuery,
      variables: {
        merchantId,
      },
    })
  }

  async getTranslates() {
    return await this.gqlClient.query({
      query: schema.GetTranslates,
    })
  }

  async saveTranslates(data) {
    return await this.gqlClient.mutate({
      mutation: schema.SaveTranslates,
      variables: {
        data,
      },
    })
  }

  async listFonts() {
    return await this.gqlClient.query({
      query: schema.ListFontsQuery,
    })
  }

  async merchantStatus(merchantId: string) {
    return await this.gqlClient.query({
      query: schema.MerchStatus,
      variables: {
        id: merchantId,
      },
    })
  }

  async uploadBulk(file: string, headers: string) {
    return await this.gqlClient.mutate({
      mutation: schema.BulkUpload,
      variables: {
        file,
        headers,
      },
    })
  }

  async getAnalytics(merchantId: string, days?: string, dateStart?: string, dateEnd?: string) {
    return await this.gqlClient.query({
      query: schema.GetAnalytics,
      variables: {
        merchantID: merchantId,
        days,
        dateStart,
        dateEnd,
      },
    })
  }

  async getProductTranslations(productId: string, skuIds: string[]) {
    const { data } = await this.gqlClient.query({
      query: schema.GetProductTranslations,
      variables: {
        productId,
        skuIds,
      },
    })
    return {
      alternateName: data.alternateName || [],
      alternateSKUsName: data.alternateSKUsName || [],
    }
  }

  async saveProductTranslation(
    productId: string,
    merchantId: string,
    translations: Array<{ code: string; name: string; description?: string }>
  ) {
    const data = JSON.stringify(translations)
    return await this.gqlClient.mutate({
      mutation: schema.SaveProductI8NFromAdmin,
      variables: {
        Id: productId,
        merchantID: merchantId,
        data,
      },
    })
  }

  async saveSKUTranslation(
    skuId: string,
    merchantId: string,
    translations: Array<{ code: string; name: string; description?: string }>
  ) {
    const data = JSON.stringify(translations)
    return await this.gqlClient.mutate({
      mutation: schema.SaveSKUI8NFromAdmin,
      variables: {
        Id: skuId,
        merchantID: merchantId,
        data,
      },
    })
  }
}

const tintVTOServiceCommon = new TintVTOService(IntegrationType.COMMON)
export const getTintVTOService = () => tintVTOServiceCommon

export const tintVTOService = getTintVTOService()
